import { Alert, Button, Card, Form, Input, Select, Space, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type GridDetail = {
  grid: { id: string; status: string; session_id: string; section_id: string; section_name?: string };
  periods: Array<{ id: string; code: string; name: string; is_break?: number | boolean }>;
  slots: Array<{
    id: string;
    day_of_week: number;
    period_id: string;
    subject_id: string;
    staff_link_id?: string | null;
    room_id?: string | null;
    notes?: string | null;
  }>;
};

type Subject = { id: string; code: string; name: string };
type Room = { id: string; code: string; name: string };
type SectionTeacher = {
  staff_link_id: string;
  subject_id: string;
  employee_code?: string;
  first_name?: string;
  last_name?: string;
  subject_code?: string;
};

type FormValues = {
  day_of_week: number;
  period_id: string;
  subject_id: string;
  staff_link_id?: string;
  room_id?: string;
  notes?: string;
};

export default function TimetableSlotFormPage() {
  const { t } = useTranslation('education');
  const { id: gridId, slotId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.timetable.manage') || user?.permissions?.includes('education.admin')
  );
  const isNew = !slotId || slotId === 'new';
  const [form] = Form.useForm<FormValues>();

  const detail = useQuery({
    queryKey: ['education-timetable-grid', gridId],
    enabled: Boolean(gridId),
    queryFn: async () => {
      const res = await api.get<GridDetail>(`/education/timetable/grids/${gridId}`);
      return res.data;
    },
  });

  const subjects = useQuery({
    queryKey: ['education-subjects'],
    queryFn: async () => {
      const res = await api.get<Subject[]>('/education/academic/subjects');
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const rooms = useQuery({
    queryKey: ['education-timetable-rooms'],
    queryFn: async () => {
      const res = await api.get<Room[]>('/education/timetable/rooms');
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const teachers = useQuery({
    queryKey: ['education-section-teachers', detail.data?.grid.section_id],
    enabled: Boolean(detail.data?.grid.section_id),
    queryFn: async () => {
      const res = await api.get<SectionTeacher[]>('/education/academic/section-teachers', {
        params: { section_id: detail.data?.grid.section_id },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  useEffect(() => {
    if (!detail.data) return;
    if (isNew) {
      form.setFieldsValue({
        day_of_week: Number(searchParams.get('day') || 1),
        period_id: searchParams.get('period_id') || undefined,
      });
      return;
    }
    const slot = detail.data.slots.find((s) => s.id === slotId);
    if (!slot) return;
    form.setFieldsValue({
      day_of_week: Number(slot.day_of_week),
      period_id: slot.period_id,
      subject_id: slot.subject_id,
      staff_link_id: slot.staff_link_id || undefined,
      room_id: slot.room_id || undefined,
      notes: slot.notes || undefined,
    });
  }, [detail.data, form, isNew, searchParams, slotId]);

  const periodOptions = useMemo(
    () =>
      (detail.data?.periods ?? [])
        .filter((p) => !(p.is_break === 1 || p.is_break === true))
        .map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` })),
    [detail.data?.periods]
  );

  const staffOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: Array<{ value: string; label: string }> = [];
    for (const row of teachers.data ?? []) {
      if (!row.staff_link_id || seen.has(row.staff_link_id)) continue;
      seen.add(row.staff_link_id);
      opts.push({
        value: row.staff_link_id,
        label: `${row.employee_code || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`.trim(),
      });
    }
    return opts;
  }, [teachers.data]);

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      isNew
        ? api.post(`/education/timetable/grids/${gridId}/slots`, values)
        : api.put(`/education/timetable/grids/${gridId}/slots/${slotId}`, values),
    onSuccess: () => {
      message.success(isNew ? t('slotCreated') : t('slotUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-timetable-grid', gridId] });
      navigate(`/education/timetable/grids/${gridId}`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={isNew ? t('addSlot') : t('editSlot')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  if (detail.data?.grid.status === 'published') {
    return (
      <EducationPageChrome title={t('editSlot')}>
        <Alert type="info" showIcon message={t('timetablePublishedLocked')} />
        <Button style={{ marginTop: 12 }} onClick={() => navigate(`/education/timetable/grids/${gridId}`)}>
          {t('backToGrid')}
        </Button>
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={isNew ? t('addSlot') : t('editSlot')}
      description={detail.data?.grid.section_name || t('timetableGridsIntro')}
      extra={
        <Button onClick={() => navigate(`/education/timetable/grids/${gridId}`)}>{t('backToGrid')}</Button>
      }
    >
      <Card size="small" style={{ maxWidth: 640 }} loading={detail.isLoading}>
        <Form form={form} layout="vertical" onFinish={(values) => save.mutate(values)}>
          <Form.Item name="day_of_week" label={t('day')} rules={[{ required: true }]}>
            <Select
              options={[1, 2, 3, 4, 5, 6, 7].map((d) => ({
                value: d,
                label: t(`weekday${d}` as 'weekday1'),
              }))}
            />
          </Form.Item>
          <Form.Item name="period_id" label={t('period')} rules={[{ required: true }]}>
            <Select options={periodOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="subject_id" label={t('subject')} rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={(subjects.data ?? []).map((s) => ({
                value: s.id,
                label: `${s.code} — ${s.name}`,
              }))}
              loading={subjects.isFetching}
            />
          </Form.Item>
          <Form.Item name="staff_link_id" label={t('teacher')}>
            <Select allowClear showSearch optionFilterProp="label" options={staffOptions} />
          </Form.Item>
          <Form.Item name="room_id" label={t('room')}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={(rooms.data ?? []).map((r) => ({
                value: r.id,
                label: `${r.code} — ${r.name}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="notes" label={t('notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              {t('save')}
            </Button>
            <Button onClick={() => navigate(`/education/timetable/grids/${gridId}`)}>{t('cancel')}</Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
